import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { StatusPill } from "@/components/StatusPill";
import { useI18n } from "@/i18n/I18nContext";
import { Users, LayoutGrid, Network, Search, Loader2, ChevronRight } from "lucide-react";
import OrgGraph from "@/components/OrgGraph";

const TYPE_KEY = {
  CORE_EXECUTIVE: "common.etExecutive", CORE_LEAD: "common.etLead", SPECIALIST: "common.etSpecialist", CONTROL: "common.etControl",
};

function AgentRow({ a, onOpen }) {
  const { t } = useI18n();
  return (
    <div className="m-card m-card-hover p-4 cursor-pointer" onClick={() => onOpen(a.code)} data-testid={`ai-employee-card-${a.code}`}>
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-xl bg-[#182238] border border-[#1E293B] flex items-center justify-center font-display font-bold text-white text-sm shrink-0">{a.name[0]}</div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2"><p className="text-white font-semibold text-sm truncate">{a.name}</p><span className="mono-label">{a.code}</span></div>
          <p className="text-xs text-[#94A3B8] truncate">{a.position}</p>
        </div>
        <div className="hidden sm:flex flex-col items-end gap-1">
          <StatusPill status={a.availability_status} />
          <span className="text-[10px] text-[#64748B]">{t(TYPE_KEY[a.employment_type])}</span>
        </div>
        <ChevronRight size={16} className="text-[#64748B]" />
      </div>
    </div>
  );
}

export default function Workforce() {
  const [agents, setAgents] = useState([]);
  const [depts, setDepts] = useState([]);
  const [view, setView] = useState("grid");
  const [q, setQ] = useState("");
  const [dept, setDept] = useState("ALL");
  const navigate = useNavigate();
  const { t } = useI18n();

  useEffect(() => {
    api.agents().then(setAgents);
    api.departments().then(setDepts);
  }, []);

  const filtered = useMemo(() => agents.filter((a) =>
    (dept === "ALL" || a.department === dept) &&
    (!q || a.name.toLowerCase().includes(q.toLowerCase()) || a.position.toLowerCase().includes(q.toLowerCase()) || a.code.toLowerCase().includes(q.toLowerCase()))
  ), [agents, q, dept]);

  if (!agents.length) return <div className="flex items-center justify-center py-32"><Loader2 className="animate-spin text-[#2563EB]" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="mono-label">MASANDI AI LABS</p>
          <h1 className="font-display text-3xl font-extrabold text-white mt-1 flex items-center gap-2"><Users className="text-[#2563EB]" /> {t("wf.title")}</h1>
          <p className="text-sm text-[#94A3B8] mt-1">{t("wf.count", { n: agents.length })}</p>
        </div>
        <div className="flex items-center gap-2 bg-[#0F1626] border border-[#1E293B] rounded-xl p-1">
          <button onClick={() => setView("grid")} className={`px-3 py-1.5 rounded-lg text-xs flex items-center gap-1.5 ${view === "grid" ? "bg-[#2563EB] text-white" : "text-[#94A3B8]"}`} data-testid="workforce-view-grid"><LayoutGrid size={14} /> {t("wf.grid")}</button>
          <button onClick={() => setView("org")} className={`px-3 py-1.5 rounded-lg text-xs flex items-center gap-1.5 ${view === "org" ? "bg-[#2563EB] text-white" : "text-[#94A3B8]"}`} data-testid="workforce-view-org"><Network size={14} /> {t("wf.org")}</button>
        </div>
      </div>

      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("wf.searchPh")} className="input-dark w-full h-10 pl-10 pr-4 text-sm" data-testid="workforce-search" />
        </div>
        <select value={dept} onChange={(e) => setDept(e.target.value)} className="input-dark h-10 px-3 text-sm" data-testid="workforce-dept-filter">
          <option value="ALL">{t("wf.allDepts")}</option>
          {depts.map((d) => <option key={d.code} value={d.code}>{d.name}</option>)}
        </select>
      </div>

      {view === "grid" ? (
        <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-3">
          {filtered.map((a) => <AgentRow key={a.code} a={a} onOpen={(c) => navigate(`/workforce/${c}`)} />)}
        </div>
      ) : (
        <OrgGraph agents={filtered} />
      )}
    </div>
  );
}
